// Authentication context - Kullanıcı giriş/çıkış yönetimi
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Authentication context hook'u
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth hook AuthProvider içinde kullanılmalıdır');
  }
  return context;
};

// Authentication provider bileşeni
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Otomatik logout - 30 dakika hareketsizlik
  useEffect(() => {
    const checkActivity = () => {
      const now = Date.now();
      const timeDiff = now - lastActivity;
      const thirtyMinutes = 30 * 60 * 1000; // 30 dakika

      if (timeDiff > thirtyMinutes && user) {
        toast.error('Güvenlik nedeniyle oturumunuz sonlandırıldı');
        logout();
      }
    };

    const interval = setInterval(checkActivity, 60000); // Her dakika kontrol et
    return () => clearInterval(interval);
  }, [lastActivity, user]);

  // Kullanıcı aktivitesini takip et
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());
    
    // Mouse ve keyboard olaylarını dinle
    document.addEventListener('mousemove', updateActivity);
    document.addEventListener('keypress', updateActivity);
    document.addEventListener('click', updateActivity);
    document.addEventListener('scroll', updateActivity);

    return () => {
      document.removeEventListener('mousemove', updateActivity);
      document.removeEventListener('keypress', updateActivity);
      document.removeEventListener('click', updateActivity);
      document.removeEventListener('scroll', updateActivity);
    };
  }, []);

  // Uygulama başladığında token kontrolü yap
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
          // Önce localStorage'dan kullanıcı bilgilerini yükle
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Sonra token'ı doğrula
          try {
            const verifiedUser = await authService.verifyToken();
            setUser(verifiedUser);
            localStorage.setItem('user', JSON.stringify(verifiedUser));
          } catch (error) {
            console.error('Token doğrulama hatası:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth init hatası:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Giriş yapma fonksiyonu
  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      
      // Token'ı localStorage'a kaydet
      localStorage.setItem('token', response.token);
      
      // Backend'den gelen veriyi frontend formatına çevir
      const userData = {
        id: response.user.id,
        user_username: response.user.username,
        user_fullname: response.user.fullname,
        user_email: response.user.email,
        user_phone: response.user.phone,
        user_gsm: response.user.phone, // user_gsm alanını da ekle
        user_type: response.user.userType,
        user_department: response.user.department,
        user_last_login: response.user.lastLogin
      };
      
      // Kullanıcı bilgilerini localStorage'a kaydet
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Kullanıcı bilgilerini state'e kaydet
      setUser(userData);
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı bilgilerini güncelleme fonksiyonu
  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  // Çıkış yapma fonksiyonu
  const logout = async (showToast = true) => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout hatası:', error);
    } finally {
      // Local storage'ı temizle
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Kullanıcı state'ini temizle
      setUser(null);
      
      // Cache'i temizle - React Query cache'ini temizle
      if (window.queryClient) {
        window.queryClient.clear();
      }
      
      if (showToast) {
        toast.success('Çıkış yapıldı');
      }
    }
  };

  // Şifre sıfırlama isteği
  const forgotPassword = async (email) => {
    try {
      await authService.forgotPassword(email);
      toast.success('Şifre sıfırlama linki email adresinize gönderildi');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Email gönderilemedi';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Şifre sıfırlama token doğrulama
  const verifyResetToken = async (token) => {
    try {
      const response = await authService.verifyResetToken(token);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Token geçersiz';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Yeni şifre belirleme
  const resetPassword = async (token, password) => {
    try {
      await authService.resetPassword(token, password);
      toast.success('Şifre başarıyla güncellendi');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Şifre güncellenemedi';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Kullanıcı profil bilgilerini güncelle
  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      setUser(response.user);
      toast.success('Profil başarıyla güncellendi');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Profil güncellenemedi';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Kullanıcı yetkisi kontrolü
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // user_type alanını kullan (veritabanından gelen alan adı)
    const userType = user.userType || user.user_type;
    
    switch (permission) {
      case 'admin':
        return userType === 'admin';
      case 'manager':
        return ['admin', 'manager'].includes(userType);
      case 'agent':
        return ['admin', 'manager', 'agent'].includes(userType);
      default:
        return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    verifyResetToken,
    resetPassword,
    updateProfile,
    hasPermission,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
